import { useCallback, useEffect, useMemo } from 'react';
import { ChartData, DashboardProps } from '@elementary/common-ui/pages';
import { colors } from '@elementary/common-ui/style/theme';
import {
  ModelRun,
  ModelRuns,
  TestInvocation,
  TestResult,
  TreeLeaf,
} from '@elementary/common-ui/types';
import {
  distinctArray,
  distinctArrayBy,
  groupBy,
  sumArr,
} from '@elementary/common-ui/utils/common';
import { TEST_TYPES } from '@elementary/common-ui/utils/testTypes';
import dayjs from 'dayjs';
import {
  DASHBOARD_FILTER_VALUE_TAGS,
  DASHBOARD_FILTER_VALUE_OWNERS,
} from '@elementary/common-ui/constants/urlParams';
import { useRecoilState } from 'recoil';
import { FilterOption } from '@elementary/common-ui/components';
import { Operator } from '@elementary/common-ui/hooks/useUrlFilters';
import { useDashboardFilters } from '@elementary/common-ui/pages/Dashboard';
import useReport from '@/shared/hooks/useReport';
import * as atoms from '@/shared/store/atoms';

export type DashboardData = Omit<DashboardProps, 'renderEmptyStatePage'>;

type TestRunWithMeta = TestInvocation & {
  modelId: string;
  testUniqueId: string;
};

const useDashboard = () => {
  const { reportData } = useReport();
  const [dashboardCache, setDashboardCache] = useRecoilState(
    atoms.dashboardDataCache,
  );
  const { urlFilters } = useDashboardFilters();

  const isEmpty = useMemo(
    () =>
      Object.keys(reportData?.test_results || {}).length === 0 &&
      (reportData?.model_runs || []).length === 0,
    [reportData],
  );

  const { tagFilter, ownerFilter } = useMemo(() => {
    const tagFilter = urlFilters?.filter((filter) => filter.property === 'tag');
    const ownerFilter = urlFilters?.filter(
      (filter) => filter.property === 'owner',
    );

    return {
      tagFilter: tagFilter,
      ownerFilter: ownerFilter,
    };
  }, [urlFilters]);

  const projName = reportData?.env?.project_name;
  const cacheKey = `${projName}-${JSON.stringify(tagFilter)}-${JSON.stringify(
    ownerFilter,
  )}`;
  const cachedData = dashboardCache[cacheKey];

  const {
    modelsData,
    modelRunsData,
    testResultsData,
    testRunsData,
    creationTime,
    sidebarsData,
    filtersData,
    daysBack,
  } = useMemo(
    () => ({
      modelsData: reportData?.models,
      modelRunsData: reportData?.model_runs,
      testResultsData: reportData?.test_results,
      testRunsData: reportData?.test_runs,
      creationTime: reportData?.creation_time,
      sidebarsData: reportData?.groups,
      filtersData: reportData?.filters,
      daysBack: reportData?.days_back,
    }),
    [reportData],
  );

  const isFiltersSelected =
    (tagFilter?.length ?? 0) > 0 || (ownerFilter?.length ?? 0) > 0;

  const allModels = useMemo(() => {
    if (cachedData) {
      return [];
    }

    const allModelsIds = filtersData?.test_results
      ?.map((statusResults) => statusResults.model_unique_ids)
      .flat()
      .filter((modelId) => modelsData?.[modelId]);

    return distinctArray(allModelsIds || []);
  }, [cachedData, filtersData, modelsData]);

  const tagsTree = useMemo(
    () =>
      sidebarsData?.data_assets?.find(
        (view) => view.name === DASHBOARD_FILTER_VALUE_TAGS,
      )?.data as Record<string, TreeLeaf[]>,
    [sidebarsData],
  );

  const ownersTree = useMemo(
    () =>
      sidebarsData?.data_assets?.find(
        (view) => view.name === DASHBOARD_FILTER_VALUE_OWNERS,
      )?.data as Record<string, TreeLeaf[]>,
    [sidebarsData],
  );

  const filteredModels = useMemo(() => {
    if (!isFiltersSelected || cachedData) return null;

    return allModels.filter((model) => {
      let tagFilterMatch = true;

      if (tagFilter) {
        tagFilter.forEach((tFilter) => {
          if (tFilter.operator === 'equals') {
            // OR logic within a single filter: at least one tag value must match
            const hasMatch = tFilter.value.some((tagValue: string) =>
              tagsTree?.[tagValue]?.find((item) => item.node_id === model),
            );
            if (!hasMatch) {
              tagFilterMatch = false;
            }
          }
          if (tFilter.operator === 'notEquals') {
            // For notEquals: model should not have ANY of the specified tags
            const hasMatch = tFilter.value.some((tagValue: string) =>
              tagsTree?.[tagValue]?.find((item) => item.node_id === model),
            );
            if (hasMatch) {
              tagFilterMatch = false;
            }
          }
        });
      }

      let ownerFilterMatch = true;
      if (ownerFilter) {
        ownerFilter.forEach((oFilter) => {
          if (oFilter.operator === 'equals') {
            // OR logic within a single filter: at least one owner value must match
            const hasMatch = oFilter.value.some((ownerValue: string) =>
              ownersTree?.[ownerValue]?.find((item) => item.node_id === model),
            );
            if (!hasMatch) {
              ownerFilterMatch = false;
            }
          }
          if (oFilter.operator === 'notEquals') {
            // For notEquals: model should not have ANY of the specified owners
            const hasMatch = oFilter.value.some((ownerValue: string) =>
              ownersTree?.[ownerValue]?.find((item) => item.node_id === model),
            );
            if (hasMatch) {
              ownerFilterMatch = false;
            }
          }
        });
      }

      return tagFilterMatch && ownerFilterMatch;
    });
  }, [
    allModels,
    cachedData,
    isFiltersSelected,
    ownerFilter,
    ownersTree,
    tagFilter,
    tagsTree,
  ]);

  const allTestResults: TestResult[] = useMemo(() => {
    const allResults: TestResult[] = [];
    if (!testResultsData || cachedData) return allResults;

    Object.keys(testResultsData).forEach((modelKey) => {
      if (!filteredModels || filteredModels.includes(modelKey)) {
        (testResultsData[modelKey] || []).forEach((res) => {
          allResults.push(res);
        });
      }
    });
    return distinctArrayBy(allResults, 'metadata.elementary_unique_id');
  }, [cachedData, filteredModels, testResultsData]);

  const chartDates: string[] = useMemo(() => {
    const arr: string[] = [];
    if (!daysBack || cachedData) return arr;

    for (let i = daysBack - 1; i >= 0; i -= 1) {
      const date = dayjs(creationTime).subtract(i, 'days').format('MM/DD');

      arr.push(date);
    }
    return arr;
  }, [cachedData, creationTime, daysBack]);

  const allTestRuns: TestRunWithMeta[] = useMemo(() => {
    const allRuns: TestRunWithMeta[] = [];
    if (!testRunsData || cachedData) return allRuns;

    Object.keys(testRunsData).forEach((modelKey) => {
      if (!filteredModels || filteredModels.includes(modelKey)) {
        testRunsData[modelKey].forEach((testRun) => {
          testRun.test_runs.invocations.forEach((inv) => {
            allRuns.push({
              ...inv,
              modelId: modelKey,
              testUniqueId: testRun.metadata.elementary_unique_id,
            });
          });
        });
      }
    });
    return allRuns;
  }, [cachedData, filteredModels, testRunsData]);

  const summaryData = useMemo(() => {
    if (cachedData) {
      return {};
    }

    const sumData: Record<
      string,
      {
        title: string;
        passed: number;
        warnings: number;
        failures: number;
        errors: number;
      }
    > = {};

    TEST_TYPES.forEach((type) => {
      sumData[type.key] = {
        title: type.title,
        passed: 0,
        warnings: 0,
        failures: 0,
        errors: 0,
      };
    });

    allTestResults.forEach((test) => {
      const matchingType = TEST_TYPES.find((type) => {
        const isInTestNames = type.testName.includes(test.metadata.test_name);
        const testNameMatch =
          type.filterOperator === 'notEquals' ? !isInTestNames : isInTestNames;

        const testSubTypeMatch = type.testSubType
          ? type.testSubType.includes(test.metadata.test_sub_type)
          : true;

        return testNameMatch && testSubTypeMatch;
      });

      const typeKey = matchingType?.key;

      if (typeKey) {
        switch (test.metadata.latest_run_status) {
          case 'pass':
            sumData[typeKey].passed += 1;
            break;
          case 'warn':
            sumData[typeKey].warnings += 1;
            break;
          case 'fail':
            sumData[typeKey].failures += 1;
            break;
          case 'error':
            sumData[typeKey].errors += 1;
            break;
          default:
            break;
        }
      }
    });

    return sumData;
  }, [allTestResults, cachedData]);

  const allTestRunsByDate = useMemo(
    () =>
      cachedData
        ? {}
        : groupBy(allTestRuns, (item) => dayjs(item.time_utc).format('MM/DD')),
    [allTestRuns, cachedData],
  );

  const testRunsCountsByDate = useMemo(() => {
    if (cachedData) {
      return {};
    }

    const testRunsCounts: Record<string, Record<string, number>> = {};
    const countsByHour = reportData?.test_runs_counts_by_hour || {};
    const tzOffset = new Date().getTimezoneOffset();

    Object.entries(countsByHour).forEach(([date, counts]) => {
      const dateObj = dayjs(date);
      dateObj.add(tzOffset, 'minutes');
      const dateKey = dayjs(dateObj).format('MM/DD');
      if (!testRunsCounts[dateKey]) {
        testRunsCounts[dateKey] = {};
      }
      Object.entries(counts).forEach(([status, count]) => {
        if (!testRunsCounts[dateKey][status]) {
          testRunsCounts[dateKey][status] = 0;
        }
        testRunsCounts[dateKey][status] += count;
      });
    });

    return testRunsCounts;
  }, [cachedData, reportData?.test_runs_counts_by_hour]);

  const allModelRuns: ModelRuns[] = useMemo(() => {
    if (!modelRunsData || cachedData) return [];

    return !filteredModels
      ? modelRunsData
      : modelRunsData.filter((modelRuns) =>
          filteredModels.includes(modelRuns.unique_id),
        );
  }, [cachedData, filteredModels, modelRunsData]);

  const allModelRunByDate = useMemo(() => {
    if (cachedData) {
      return {};
    }

    const flatModelRuns: ModelRun[] = (allModelRuns || [])
      ?.map((modelRuns) => modelRuns.runs)
      .flat();
    return groupBy(flatModelRuns, (item) =>
      dayjs(item.time_utc).format('MM/DD'),
    );
  }, [allModelRuns, cachedData]);

  const getTestRunsStatusChartData = useCallback(
    (status: string) => {
      const chartData: ChartData = [];

      chartDates.forEach((date) => {
        const statusTests = allTestRunsByDate[date]?.filter(
          (test) => test.status === status,
        );
        const distinctByTestId = statusTests
          ? distinctArrayBy(statusTests, 'testUniqueId')
          : [];
        const testsByDatesCount = testRunsCountsByDate[date]?.[status] ?? 0;
        chartData.push({
          date,
          value: distinctByTestId.length + testsByDatesCount,
        });
      });

      return chartData;
    },
    [testRunsCountsByDate, allTestRunsByDate, chartDates],
  );

  const failedTestsChartData = useMemo(
    () => (cachedData ? [] : getTestRunsStatusChartData('fail')),
    [cachedData, getTestRunsStatusChartData],
  );

  const warningTestsChartData = useMemo(
    () => (cachedData ? [] : getTestRunsStatusChartData('warn')),
    [cachedData, getTestRunsStatusChartData],
  );

  const tablesWithTestFailuresChartData = useMemo(() => {
    if (cachedData) {
      return [];
    }

    const chartData: ChartData = [];

    chartDates.forEach((date) => {
      const failedTests =
        allTestRunsByDate[date]?.filter((test) =>
          ['fail', 'error'].includes(test.status),
        ) || [];
      const distinctByModel = distinctArrayBy(failedTests, 'modelId');

      chartData.push({ date, value: distinctByModel.length });
    });

    return chartData;
  }, [allTestRunsByDate, cachedData, chartDates]);

  const monitoredTablesChartData = useMemo(() => {
    if (cachedData) {
      return [];
    }

    const chartData: ChartData = [];
    const totalModels = (filteredModels || allModels).length;

    chartDates.forEach((date) => {
      const filteredTestRuns =
        allTestRunsByDate[date]?.filter((test) => modelsData?.[test.modelId]) ||
        [];

      const distinctByModel = distinctArrayBy(filteredTestRuns, 'modelId');
      const percentage = (distinctByModel.length / totalModels) * 100;

      chartData.push({ date, value: percentage });
    });

    return chartData;
  }, [
    cachedData,
    filteredModels,
    allModels,
    chartDates,
    allTestRunsByDate,
    modelsData,
  ]);

  const uniqueTestExecData = useMemo(() => {
    if (cachedData) {
      return [];
    }

    const chartData: ChartData = [];

    chartDates.forEach((date) => {
      const distinctByTestId = allTestRunsByDate[date]
        ? distinctArrayBy(allTestRunsByDate[date], 'testUniqueId')
        : [];
      const testsByDatesCount = sumArr(
        Object.values(testRunsCountsByDate?.[date] || {}),
      );
      chartData.push({
        date,
        value: distinctByTestId.length + testsByDatesCount,
      });
    });

    return chartData;
  }, [cachedData, chartDates, allTestRunsByDate, testRunsCountsByDate]);

  const resultsBreakdownData = useMemo(() => {
    if (cachedData) {
      return [];
    }

    let passed = 0;
    let warning = 0;
    let failed = 0;
    let error = 0;

    allTestResults.forEach((test) => {
      switch (test.metadata.latest_run_status) {
        case 'pass':
          passed += 1;
          break;
        case 'warn':
          warning += 1;
          break;
        case 'fail':
          failed += 1;
          break;
        case 'error':
          error += 1;
          break;
        default:
          break;
      }
    });

    return [
      { name: 'Passed', value: passed, color: '#42CB9A' },
      { name: 'Warning', value: warning, color: '#FFA621' },
      { name: 'Failed', value: failed, color: '#FF655B' },
      { name: 'Error', value: error, color: '#D4DCE7' },
    ];
  }, [allTestResults, cachedData]);

  const tablesHealthData = useMemo(() => {
    if (cachedData) {
      return [];
    }

    const failures = filtersData?.test_results
      ?.find((filter) => filter.name === 'failures')
      ?.model_unique_ids.filter(
        (model) =>
          modelsData?.[model] &&
          (!filteredModels || filteredModels.includes(model)),
      );

    const warnings = filtersData?.test_results
      ?.find((filter) => filter.name === 'warnings')
      ?.model_unique_ids.filter((model) => !failures?.includes(model))
      .filter(
        (model) =>
          modelsData?.[model] &&
          (!filteredModels || filteredModels.includes(model)),
      );

    const passed = filtersData?.test_results
      ?.find((filter) => filter.name === 'passed')
      ?.model_unique_ids.filter(
        (model) => !failures?.includes(model) && !warnings?.includes(model),
      )
      .filter(
        (model) =>
          modelsData?.[model] &&
          (!filteredModels || filteredModels.includes(model)),
      );

    return [
      { name: 'Passed', value: passed?.length || 0, color: '#42CB9A' },
      { name: 'Warning', value: warnings?.length || 0, color: '#FFA621' },
      { name: 'Failed', value: failures?.length || 0, color: '#FF655B' },
    ];
  }, [cachedData, filtersData, modelsData, filteredModels]);

  const monitoredTablesData = useMemo(() => {
    if (cachedData) {
      return [];
    }

    const failures =
      filtersData?.test_results
        ?.find((filter) => filter.name === 'failures')
        ?.model_unique_ids.filter(
          (model) =>
            modelsData?.[model] &&
            (!filteredModels || filteredModels.includes(model)),
        ) || [];

    const warnings =
      filtersData?.test_results
        ?.find((filter) => filter.name === 'warnings')
        ?.model_unique_ids.filter(
          (model) =>
            modelsData?.[model] &&
            (!filteredModels || filteredModels.includes(model)),
        ) || [];

    const passed =
      filtersData?.test_results
        ?.find((filter) => filter.name === 'passed')
        ?.model_unique_ids.filter(
          (model) =>
            modelsData?.[model] &&
            (!filteredModels || filteredModels.includes(model)),
        ) || [];

    const unmonitored =
      filtersData?.test_results
        ?.find((filter) => filter.name === 'no_test')
        ?.model_unique_ids.filter(
          (model) =>
            modelsData?.[model] &&
            (!filteredModels || filteredModels.includes(model)),
        ) || [];

    const joinedItems = failures.concat(warnings).concat(passed);
    const monitored = distinctArray(joinedItems);

    return [
      { name: 'Monitored', value: monitored.length, color: '#42CB9A' },
      { name: 'Unmonitored', value: unmonitored.length, color: '#D4DCE7' },
    ];
  }, [cachedData, filtersData, modelsData, filteredModels]);

  const modelsHealthData = useMemo(() => {
    if (cachedData) {
      return [];
    }

    const modelByStatues = groupBy(allModelRuns, (item) => item.status);

    return [
      {
        name: 'Success',
        value: (modelByStatues.success || []).length,
        color: colors.green,
      },
      {
        name: 'Error',
        value: (modelByStatues.error || []).length,
        color: colors.red,
      },
      {
        name: 'Skipped',
        value: (modelByStatues.skipped || []).length,
        color: colors.gray1,
      },
    ];
  }, [allModelRuns, cachedData]);

  const modelFailuresChartData = useMemo(() => {
    if (cachedData) {
      return [];
    }

    const chartData: ChartData = [];

    chartDates.forEach((date) => {
      const failedModelRuns =
        allModelRunByDate[date]?.filter(
          (modelRun) => modelRun.status === 'error',
        ) || [];

      chartData.push({ date, value: failedModelRuns.length });
    });

    return chartData;
  }, [allModelRunByDate, cachedData, chartDates]);

  const tagFilterOptions = useMemo(
    () => (cachedData ? [] : Object.keys(tagsTree || {})),
    [cachedData, tagsTree],
  );

  const ownerFilterOptions = useMemo(
    () => (cachedData ? [] : Object.keys(ownersTree || {})),
    [cachedData, ownersTree],
  );

  const statusChartsDomainYDataMax = useMemo(() => {
    if (cachedData) {
      return 0;
    }

    const failuresMax = Math.max(
      ...failedTestsChartData.map((item) => item.value),
    );

    const warningsMax = Math.max(
      ...warningTestsChartData.map((item) => item.value),
    );

    return Math.max(failuresMax, warningsMax);
  }, [cachedData, failedTestsChartData, warningTestsChartData]);

  const filterOptions = useMemo(() => {
    if (cachedData) {
      return [] as FilterOption[];
    }

    return [
      {
        propertyLabel: 'Tag',
        property: 'tag',
        options: tagFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'] as Operator[],
      },
      {
        propertyLabel: 'Owner',
        property: 'owner',
        options: ownerFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'] as Operator[],
      },
    ];
  }, [cachedData, ownerFilterOptions, tagFilterOptions]);

  const dashboardData: DashboardData = useMemo(
    () => ({
      filterOptions,
      resultsBreakdownData,
      tablesHealthData,
      monitoredTablesData,
      modelsHealthData,
      summaryData,
      uniqueTestExecData,
      failedTestsChartData,
      warningTestsChartData,
      monitoredTablesChartData,
      tablesWithTestFailuresChartData,
      modelFailuresChartData,
      statusChartsDomainYDataMax,
      testTypes: TEST_TYPES,
      daysBack,
      invocationData: reportData?.invocation,
      isEmpty,
    }),
    [
      daysBack,
      failedTestsChartData,
      filterOptions,
      isEmpty,
      modelFailuresChartData,
      modelsHealthData,
      monitoredTablesChartData,
      monitoredTablesData,
      reportData?.invocation,
      resultsBreakdownData,
      statusChartsDomainYDataMax,
      summaryData,
      tablesHealthData,
      tablesWithTestFailuresChartData,
      uniqueTestExecData,
      warningTestsChartData,
    ],
  );

  useEffect(() => {
    if (reportData && !dashboardCache[cacheKey]) {
      setDashboardCache((prev) => ({
        ...prev,
        [cacheKey]: dashboardData,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData]);

  return cachedData || dashboardData;
};

export default useDashboard;
